/**
 * Find a Dealer — search, tier filter, state shading, pin placement, zoom/pan
 * and distance sorting.
 *
 * SEARCH HAS TWO MODES and the note under the count says which one is running,
 * so it is never mysterious:
 *   - GEO. What was typed resolved to a place — a US ZIP prefix or Canadian FSA
 *     out of `postcode-centroids.json`, or a state/province read back off the
 *     map's own geometry. Every location in the current tier is listed, sorted
 *     by great-circle distance from that place, with the distance on each card.
 *   - TEXT. It did not resolve, so the substring filter over `data-haystack`
 *     runs exactly as it always has.
 * The table is fetched only when someone types something postcode-shaped, and if
 * that fetch fails the search silently falls back to TEXT rather than breaking.
 *
 * The projection constants below are GENERATED by `migration/build-dealer-map.mjs`
 * from the same d3 projection that drew `assets/dealer-map-na.svg`. Do not edit
 * them by hand and do not edit the map without re-running that script: it
 * asserts this arithmetic against d3 across every state and province centroid
 * plus 44 cities and refuses to write if they disagree by more than a
 * thousandth of a pixel. A pin placed against a subtly wrong projection lands
 * somewhere plausible and wrong, which is the failure nobody notices.
 *
 * The map covers the United States and Canada. Alaska and Hawaii are insets, so
 * a dealer's lobe has to be chosen before it can be projected — and one case
 * cannot be settled from coordinates alone: the Alaska panhandle sits inside
 * British Columbia's bounding box (Juneau is -134.4, 58.3; BC spans
 * -138.9..-114.0, 48.3..60.1). That is why a dealer's state/province decides
 * the lobe when it is filled in, and bounding boxes are only the fallback.
 *
 * No JSON is embedded in a script tag anywhere: dealer data rides on data-*
 * attributes. A dealer's own name containing "</script>" would otherwise end
 * the document early, and the page would look fine until the one dealer who
 * breaks it.
 */

/* BEGIN GENERATED — build-dealer-map.mjs */
const VIEW = { w: 975, h: 785 };
const LOBES = [
  {
    "key": "main",
    "n": 0.655787802196,
    "C": 1.403369765915,
    "lambda0": -98,
    "centerLon": 0,
    "centerLat": 33.6442,
    "k": 1106.757619,
    "tx": 406.227763,
    "ty": 621.476333
  },
  {
    "key": "alaska",
    "n": 0.862729915663,
    "C": 1.742403876506,
    "lambda0": -154,
    "centerLon": 0,
    "centerLat": 33.6442,
    "k": 275.590551,
    "tx": 879.25,
    "ty": 762.6
  },
  {
    "key": "hawaii",
    "n": 0.224095047668,
    "C": 1.043006853357,
    "lambda0": -157,
    "centerLon": 0,
    "centerLat": 33.6442,
    "k": 1178.509532,
    "tx": 899.45,
    "ty": 469.5
  }
];
const LOBE_BOXES = {"main":[-141,-52,24,84],"alaska":[-180,-129,51,72],"hawaii":[-161,-154,18,23]};
/* END GENERATED */

const RAD = Math.PI / 180;

function projectInto(lobe, lon, lat) {
  const rho = (phi) => Math.sqrt(lobe.C - 2 * lobe.n * Math.sin(phi)) / lobe.n;
  const theta0 = lobe.n * (lobe.centerLon * RAD);
  const r0 = rho(lobe.centerLat * RAD);
  const offX = r0 * Math.sin(theta0);
  const offY = rho(0) - r0 * Math.cos(theta0);
  const theta = lobe.n * (lon * RAD - lobe.lambda0 * RAD);
  const r = rho(lat * RAD);
  return [
    (r * Math.sin(theta) - offX) * lobe.k + lobe.tx,
    (offY - (rho(0) - r * Math.cos(theta))) * lobe.k + lobe.ty,
  ];
}

/**
 * @param {string|null} code Region code such as "US-AK" — decides the lobe when
 *   known, because bounding boxes cannot separate the Alaska panhandle from BC.
 * @returns {[number, number] | null} null when the point is off the map.
 */
export function project(lon, lat, code) {
  const forced = code === 'US-AK' ? 'alaska' : code === 'US-HI' ? 'hawaii' : code ? 'main' : null;
  for (const lobe of LOBES) {
    if (forced) {
      if (lobe.key !== forced) continue;
    } else {
      const box = LOBE_BOXES[lobe.key];
      if (!box || lon < box[0] || lon > box[1] || lat < box[2] || lat > box[3]) continue;
    }
    const p = projectInto(lobe, lon, lat);
    // Belt and braces: a point outside the viewBox is not a pin, it is a data
    // error, and it should disappear rather than sit on the edge.
    if (p[0] < 0 || p[0] > VIEW.w || p[1] < 0 || p[1] > VIEW.h) continue;
    return p;
  }
  return null;
}

/**
 * Inverse of `project`, for one named lobe. Closed form — an Albers equal-area
 * conic inverts exactly, no iteration.
 *
 * This exists so a typed state or province can become a coordinate without
 * shipping a second gazetteer: the map already knows where every region is, and
 * `path.getBBox()` gives its centre in map pixels. Reading that back out is
 * cheaper and cannot drift from the geometry the way a hand-typed table would.
 *
 * The lobe cannot be guessed from x/y, because the Alaska and Hawaii insets are
 * drawn in open Atlantic — a point inside the Alaska frame is a perfectly valid
 * main-lobe point too, and would invert to somewhere off Newfoundland. Every
 * state path carries `data-lobe` for exactly this reason; pass it.
 */
export function unproject(x, y, lobeKey) {
  const lobe = LOBES.find((l) => l.key === lobeKey);
  if (!lobe) return null;
  const rho = (phi) => Math.sqrt(lobe.C - 2 * lobe.n * Math.sin(phi)) / lobe.n;
  const theta0 = lobe.n * (lobe.centerLon * RAD);
  const r0 = rho(lobe.centerLat * RAD);
  const offX = r0 * Math.sin(theta0);
  const offY = rho(0) - r0 * Math.cos(theta0);
  // Undo the affine, then read the polar pair straight back off.
  const a = (x - lobe.tx) / lobe.k + offX;          // r sin(theta)
  const b = (y - lobe.ty) / lobe.k - offY + rho(0); // r cos(theta)
  const r = Math.hypot(a, b);
  const lon = Math.atan2(a, b) / lobe.n / RAD + lobe.lambda0;
  // rho = sqrt(C - 2n sin(phi))/n, rearranged for phi.
  const sinPhi = (lobe.C - r * r * lobe.n * lobe.n) / (2 * lobe.n);
  if (!(sinPhi >= -1 && sinPhi <= 1)) return null;
  return [lon, Math.asin(sinPhi) / RAD];
}

/**
 * Great-circle distance in miles. NOT pixel distance: the map has three lobes at
 * three different scales, so Anchorage and Denver are 60px apart on screen and
 * 2,400 miles apart on the planet. Measuring the screen would sort the list into
 * a confident lie.
 *
 * The radius is the one `migration/build-postcode-table.mjs` uses, so the numbers
 * shown here are on the same footing as that script's published error budget.
 */
const EARTH_MI = 3958.7613;
const MI_PER_KM = 0.621371192;

export function haversineMi(aLat, aLon, bLat, bLon) {
  const dLat = (bLat - aLat) * RAD;
  const dLon = (bLon - aLon) * RAD;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(aLat * RAD) * Math.cos(bLat * RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_MI * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * What someone typed -> a key in `postcode-centroids.json`, or null.
 *
 * The whole string has to look like a postcode, not merely start like one. A
 * shop called "A1 Truck Works" normalises to "A1TRUCKWORKS", which starts with
 * a valid Canadian FSA pattern — accepting a prefix match would silently sort
 * the list by distance from somewhere in Alberta because of a shop's name.
 *
 * Lengths accepted: 3 (bare prefix / FSA), 5 (US ZIP), 6 (full postal code),
 * 9 (ZIP+4). Four digits is ambiguous garbage and is refused rather than guessed.
 */
export function postcodeKey(raw) {
  const s = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (/^\d{3}$|^\d{5}$|^\d{9}$/.test(s)) return s.slice(0, 3);
  if (/^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(s)) return s.slice(0, 3);
  return null;
}

/**
 * Which lobe a US ZIP prefix belongs to. Same rule
 * `migration/build-postcode-table.mjs` uses to bucket its own error statistics,
 * so the two cannot disagree about where Alaska starts.
 *
 * Without this, Juneau's 998 prefix (-134.4, 58.3) sits inside the main lobe's
 * bounding box and would be drawn in British Columbia.
 */
const lobeHintFor = (key) => (/^99[5-9]$/.test(key) ? 'US-AK' : /^96[78]$/.test(key) ? 'US-HI' : 'US-ZZ');

/** Past this, "near you" is not a claim worth making on a four-dealer map. */
const NEAR_RADIUS_MI = 150;

const SVG_NS = 'http://www.w3.org/2000/svg';

/* Tip at exactly 0,0 so the pin points at the place. Four tiers, four
   readings:
     stocking   — solid teardrop, shield glyph. Dethloff sells armour.
     installing — same solid teardrop, wrench glyph. They will fit it for you.
                  Same silhouette as stocking because it is the same errand:
                  drive here. Only what they DO there differs, so only the glyph
                  differs.
     pickup     — same teardrop inverted, roof glyph. The shop itself, not a
                  dealer, so it gets the building rather than the shield.
     online     — a ring and no tail: "a company is here", not "drive here".
   The three you can walk into share a silhouette on purpose; the one you cannot
   does not. */
const PIN_BODY = 'M0,0 C-7,-8.6 -10.5,-11.5 -10.5,-16 A10.5,10.5 0 1,1 10.5,-16 C10.5,-11.5 7,-8.6 0,0 Z';
const PIN_GLYPH = 'M0,-21.6 L5.4,-19.8 L5.4,-15.4 C5.4,-12.4 3.1,-10.6 0,-9.6 C-3.1,-10.6 -5.4,-12.4 -5.4,-15.4 L-5.4,-19.8 Z';
const PIN_GLYPH_PICKUP = 'M-5.8,-16.2 L0,-21 L5.8,-16.2 M-4.1,-16.2 L-4.1,-10.6 L4.1,-10.6 L4.1,-16.2';
/* An open-ended wrench on the diagonal. Drawn as one filled path rather than a
   stroke: at this size a stroked outline closes up into a blob. */
const PIN_GLYPH_INSTALLING = 'M3.4,-20.4 A3.6,3.6 0 1,0 1.1,-14.2 L-3.6,-9.5 A1.7,1.7 0 1,0 -1.3,-7.2 L3.4,-11.9 A3.6,3.6 0 0,0 5.7,-18.1 L3.6,-16 L1.3,-18.3 Z';

/** Tiers whose pin is a place you can physically go, so they shade a region.
 *  An authorized installer is emphatically one of those — it is the tier where
 *  physically going is the entire point. */
const WALK_IN = new Set(['stocking', 'installing', 'pickup']);

const el = (name, attrs) => {
  const n = document.createElementNS(SVG_NS, name);
  for (const k in attrs) n.setAttribute(k, attrs[k]);
  return n;
};

/** "United States" / "USA" / "US" -> US. Bare "CA" is ISO for Canada. */
function normCountry(raw) {
  const s = (raw || '').trim().toLowerCase().replace(/\./g, '');
  if (!s) return null;
  if (/^(us|usa|united states|united states of america|america)$/.test(s)) return 'US';
  if (/^(ca|can|canada)$/.test(s)) return 'CA';
  return null;
}

const normRegion = (raw) => (raw || '').trim().toLowerCase().replace(/[^a-z ]/g, '').replace(/\s+/g, ' ');

class DealerLocator extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-dealer]')];
    this.countEl = this.querySelector('[data-count]');
    this.noResults = this.querySelector('[data-noresults]');
    this.search = this.querySelector('[data-search]');
    this.list = this.querySelector('[data-list]');
    this.popup = this.querySelector('[data-dl-popup]');
    this.popupBody = this.querySelector('[data-dl-popup-body]');
    this.geoEl = this.querySelector('[data-geo]');
    this.tier = 'all';
    this.states = [];
    this.byCode = new Map();
    this.byRegion = new Map();
    // undefined = never asked for, null = asked and it failed. Both mean "fall
    // back to text"; only the first means "try".
    this.pcTable = undefined;

    if (!this.cards.length) return;

    // Where Marcus put each block. Distance sorting reorders the DOM, so the
    // authored order has to be remembered somewhere to get back to it.
    this.cards.forEach((c, i) => { c.__i = i; });

    // The theme editor tears a section down and rebuilds it on every edit. The
    // drag handlers live on window, so without this they accumulate one set per
    // edit and the oldest ones keep a dead SVG alive.
    this.ac = new AbortController();
    this.on = { signal: this.ac.signal };

    this.#loadMap();

    this.querySelector('[data-dl-popup-close]')
      ?.addEventListener('click', () => this.#closePopup(), this.on);
    // Escape closes it; a click anywhere that is not a pin or the popup closes
    // it too. Pins stop their own clicks reaching here by opening on the way up.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#closePopup();
    }, this.on);
    document.addEventListener('click', (e) => {
      if (this.popup?.hidden) return;
      if (this.popup.contains(e.target) || e.target.closest?.('.dmfg-dl__pin')) return;
      this.#closePopup();
    }, this.on);
    window.addEventListener('resize', () => this.#positionPopup(), this.on);

    this.search?.addEventListener('input', () => this.#apply(), this.on);
    this.querySelectorAll('[data-tier]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.#closePopup();
        this.tier = btn.dataset.tier;
        this.querySelectorAll('[data-tier]').forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', String(on));
        });
        this.#apply();
      }, this.on);
    });

    this.#apply();
  }

  disconnectedCallback() {
    this.ac?.abort();
  }

  /**
   * Fetch and inject the map, then pin. Shopify caps `inline_asset_content` at
   * 15,360 bytes, so the SVG cannot be rendered into the page by Liquid. If
   * this fails the list is already server-rendered and keeps working.
   */
  async #loadMap() {
    const panel = this.querySelector('[data-map-src]');
    const slot = this.querySelector('[data-map-slot]');
    if (!panel || !slot) return;
    try {
      const res = await fetch(panel.dataset.mapSrc);
      if (!res.ok) throw new Error(`map ${res.status}`);
      const svg = await res.text();
      if (!svg.includes('<svg')) throw new Error('map is not an svg');
      slot.innerHTML = svg;
      this.pinLayer = slot.querySelector('[data-pins]');
      this.#indexStates(slot);
      this.#buildPins();
      this.#initZoom(slot.querySelector('svg'));
      this.#apply();
    } catch (err) {
      console.warn('[dealer-locator] map unavailable:', err);
      panel.hidden = true;
    }
  }

  /**
   * The name-to-code table comes off the map itself rather than being retyped
   * here, so the two cannot drift apart. Marcus can write "AB", "Alberta",
   * "CA" or "California" in the theme editor and all four resolve.
   */
  #indexStates(slot) {
    this.states = [...slot.querySelectorAll('[data-code]')];
    for (const p of this.states) {
      const code = p.dataset.code;
      this.byCode.set(code, p);
      // Both sides go through normRegion — "CA" indexed raw against "ca" looked
      // up is a miss that shows as a map with nothing shaded and no error.
      const abbr = normRegion(code.slice(3));
      for (const key of [`${p.dataset.country}|${abbr}`, `${p.dataset.country}|${normRegion(p.dataset.name)}`]) {
        this.byRegion.set(key, code);
      }
      // Also without a country, for cards where Marcus left it blank. No US
      // postal abbreviation collides with a Canadian one and no two names
      // collide, so this stays unambiguous.
      for (const key of [`|${abbr}`, `|${normRegion(p.dataset.name)}`]) {
        if (!this.byRegion.has(key)) this.byRegion.set(key, code);
      }
    }
    for (const card of this.cards) {
      card.__code = this.#codeFor(card);
      // A region that does not resolve costs the dealer its shading and, in the
      // Alaska panhandle, its pin. Silence here reads as "no dealers there".
      if (!card.__code && card.dataset.region) {
        console.warn(`[dealer-locator] "${card.dataset.name}": no state or province matches "${card.dataset.region}" in "${card.dataset.country || 'no country'}"`);
      }
    }
  }

  #codeFor(card) {
    const region = normRegion(card.dataset.region);
    if (!region) return null;
    const country = normCountry(card.dataset.country);
    return this.byRegion.get(`${country || ''}|${region}`) ?? this.byRegion.get(`|${region}`) ?? null;
  }

  #buildPins() {
    if (!this.pinLayer) return;
    // Draw order is visual priority: online underneath, walk-ins on top.
    const weight = (c) => (c.dataset.tier === 'online' ? 0
      : c.dataset.tier === 'pickup' ? 3
      : c.dataset.tier === 'installing' ? 2
      : 1);
    const ordered = [...this.cards].sort((a, b) => weight(a) - weight(b));

    for (const card of ordered) {
      const lat = parseFloat(card.dataset.lat);
      const lng = parseFloat(card.dataset.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const xy = project(lng, lat, card.__code);
      if (!xy) continue; // off the map — listed, not pinned

      const tier = ['online', 'pickup', 'installing'].includes(card.dataset.tier)
        ? card.dataset.tier
        : 'stocking';
      const g = el('g', {
        class: `dmfg-dl__pin dmfg-dl__pin--${tier}`,
        transform: `translate(${xy[0].toFixed(1)},${xy[1].toFixed(1)})`,
        tabindex: '0',
        role: 'button',
        'aria-label': card.dataset.name || '',
      });
      // Kept off the transform string so zooming can rewrite the scale without
      // having to parse its own output back out again.
      g.__x = +xy[0].toFixed(1);
      g.__y = +xy[1].toFixed(1);

      // An invisible hit target, first so it sits UNDER the artwork and cannot
      // steal the glyph's own paint. The online tier draws a 5.5-radius circle:
      // an 11px target that is hard to hit with a mouse and much worse with a
      // thumb. The teardrop pins are taller but no wider. This gives every tier
      // the same generous area to click without changing what any of them look
      // like. `fill: none` would not receive the click, so it is transparent.
      g.appendChild(el('circle', {
        class: 'dmfg-dl__pin-hit', r: 15, cx: 0, cy: -9, fill: 'transparent',
      }));

      if (tier === 'online') {
        g.appendChild(el('circle', { class: 'dmfg-dl__pin-body', r: 5.5 }));
      } else {
        g.appendChild(el('path', { class: 'dmfg-dl__pin-body', d: PIN_BODY }));
        /* Trevor, 2026-08-27: "eliminate the wrench icon and just use a solid
           blue pin." The installing tier draws no glyph at all — the teardrop
           alone. PIN_GLYPH_INSTALLING is kept above rather than deleted because
           the legend in sections/dealer-locator.liquid has to match this exactly,
           and a reader comparing the two files should see why one is unused. */
        const glyph = tier === 'pickup' ? PIN_GLYPH_PICKUP : tier === 'installing' ? null : PIN_GLYPH;
        if (glyph) g.appendChild(el('path', { class: 'dmfg-dl__pin-glyph', d: glyph }));
      }

      // WHO is there, then where. The business name is what someone came for —
      // the city is already legible from the pin's position on the map.
      const place = card.dataset.place
        || this.byCode.get(card.__code)?.dataset.name
        || card.dataset.region
        || '';
      const lines = [card.dataset.name || '', place].filter(Boolean);
      if (lines.length) {
        const t = el('text', { class: 'dmfg-dl__pin-label' });
        lines.forEach((line, i) => {
          const span = el('tspan', { x: 0, dy: i === 0 ? 0 : '1.15em', class: i ? 'dmfg-dl__pin-label-sub' : '' });
          span.textContent = line;
          t.appendChild(span);
        });
        g.appendChild(t);
        g.__label = t;
        g.__lines = lines;
        // Width is measured with getBBox on first layout, not counted in
        // characters — see #layoutLabels. Estimating by character count was
        // measured against the real thing here and is out by up to 8px in both
        // directions, which is the difference between a label that fits and one
        // that overprints its neighbour.
      }

      const t = document.createElementNS(SVG_NS, 'title');
      t.textContent = card.dataset.name || '';
      g.appendChild(t);

      const focus = () => this.#focus(card, g);
      g.addEventListener('click', () => {
        // A pan that happens to start or end on a pin is a pan, not a pick.
        // Without this, dragging the map by a pin selects that dealer and
        // recentres the view on it mid-drag.
        if (this.suppressClick) { this.suppressClick = false; return; }
        focus();
      }, this.on);
      g.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); focus(); }
      }, this.on);

      card.__pin = g;
      this.pinLayer.appendChild(g);
    }
  }

  /**
   * Zoom and pan by rewriting the viewBox. Nothing else moves, so the map stays
   * one SVG with no transform stack to reason about, and a pin's own translate
   * still means what it says.
   *
   * Deliberately NOT wheel-zoom. This sits in the middle of a scrolling page,
   * and a map that eats the scroll wheel traps the reader — they flick to get
   * past it and the map zooms instead. Buttons, drag, double-click and click-a-
   * region are all explicit.
   */
  #initZoom(svg) {
    this.svg = svg;
    this.view = { x: 0, y: 0, w: VIEW.w, h: VIEW.h };
    this.MIN_W = VIEW.w / 8; // any closer and the geometry's own resolution shows

    const apply = () => {
      const v = this.view;
      svg.setAttribute('viewBox', `${v.x.toFixed(1)} ${v.y.toFixed(1)} ${v.w.toFixed(1)} ${v.h.toFixed(1)}`);
      // Counter-scale the markers so a pin is the same size on screen at every
      // zoom level. Without this, zooming in inflates the pins faster than the
      // map and they swallow the thing you zoomed in to look at.
      const k = v.w / VIEW.w;
      const place = (p) => p && p.setAttribute('transform', `translate(${p.__x},${p.__y}) scale(${k.toFixed(4)})`);
      for (const card of this.cards) place(card.__pin);
      place(this.mark);
      this.zoomed = v.w < VIEW.w - 0.5;
      svg.classList.toggle('is-zoomed', this.zoomed);
      this.#layoutLabels();
      const reset = this.querySelector('[data-zoom="reset"]');
      if (reset) reset.disabled = !this.zoomed;
    };
    // Every zoom, drag and region click ends here, so wrapping it once keeps an
    // open popup stuck to its pin instead of stranded where the pin used to be.
    this.applyView = () => { apply(); this.#positionPopup(); };

    /** Zoom about a point in map coordinates, keeping that point still. */
    const zoomAt = (factor, cx, cy) => {
      const v = this.view;
      const w = Math.max(this.MIN_W, Math.min(VIEW.w, v.w * factor));
      const scale = w / v.w;
      const h = v.h * scale;
      v.x = cx - (cx - v.x) * scale;
      v.y = cy - (cy - v.y) * scale;
      v.w = w; v.h = h;
      this.#clamp();
      apply();
    };
    this.zoomAt = zoomAt;

    this.querySelectorAll('[data-zoom]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = this.view;
        const mode = btn.dataset.zoom;
        this.viewFromSearch = false; // the reader owns the view from here
        if (mode === 'reset') { this.view = { x: 0, y: 0, w: VIEW.w, h: VIEW.h }; apply(); return; }
        zoomAt(mode === 'in' ? 1 / 1.6 : 1.6, v.x + v.w / 2, v.y + v.h / 2);
      }, this.on);
    });

    /** Screen pixels -> map units, so a drag tracks the cursor exactly. */
    const perPx = () => this.view.w / (svg.getBoundingClientRect().width || VIEW.w);

    let drag = null;
    const endDrag = (moved) => {
      if (!drag) return;
      // A drag that ends over a region or a pin must not also count as a click
      // on it. Consumed by whichever handler fires next; a release over open
      // water leaves it set, and the next pointerup clears it before its click.
      this.suppressClick = moved;
      drag = null;
      svg.classList.remove('is-grabbing');
    };

    svg.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      drag = { x: e.clientX, y: e.clientY, vx: this.view.x, vy: this.view.y, moved: false };
      svg.classList.add('is-grabbing');
    }, this.on);
    // Listened on the window, not the SVG: releasing outside the map must still
    // end the drag, or the map keeps following the cursor with no button down.
    window.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) { drag.moved = true; this.viewFromSearch = false; }
      const s = perPx();
      this.view.x = drag.vx - dx * s;
      this.view.y = drag.vy - dy * s;
      this.#clamp();
      apply();
    }, this.on);
    window.addEventListener('pointerup', () => endDrag(drag?.moved), this.on);
    // The browser takes the gesture back — a touch that turned into a page
    // scroll, or the pointer leaving the window. No pointerup follows, so
    // without this the grab cursor sticks and the next stray move pans.
    window.addEventListener('pointercancel', () => endDrag(false), this.on);

    svg.addEventListener('dblclick', (e) => {
      const p = this.#toMap(e);
      if (p) zoomAt(1 / 1.8, p.x, p.y);
    }, this.on);

    // Click a state or province to frame it. The reference calls for region
    // zoom rather than only a global view, and this is the cheapest form of it
    // that needs no extra data — the geometry already knows its own bounds.
    for (const path of this.states) {
      path.addEventListener('click', () => {
        if (this.suppressClick) { this.suppressClick = false; return; }
        const b = path.getBBox();
        const pad = Math.max(b.width, b.height) * 0.35;
        this.#frame(b.x - pad, b.y - pad, b.width + pad * 2, b.height + pad * 2);
        this.viewFromSearch = false;
      }, this.on);
    }

    apply();
  }

  /** Fit a box, preserving the map's aspect ratio and staying inside it. */
  #frame(x, y, w, h) {
    const ratio = VIEW.h / VIEW.w;
    let vw = Math.max(w, h / ratio);
    vw = Math.max(this.MIN_W, Math.min(VIEW.w, vw));
    const vh = vw * ratio;
    this.view = { x: x + w / 2 - vw / 2, y: y + h / 2 - vh / 2, w: vw, h: vh };
    this.#clamp();
    this.applyView();
  }

  /** Keep the frame over the map. Panning into empty space is a dead end. */
  #clamp() {
    const v = this.view;
    v.x = Math.max(0, Math.min(VIEW.w - v.w, v.x));
    v.y = Math.max(0, Math.min(VIEW.h - v.h, v.y));
  }

  /** Pointer event -> map coordinates. */
  #toMap(e) {
    if (!this.svg) return null;
    const r = this.svg.getBoundingClientRect();
    if (!r.width) return null;
    return {
      x: this.view.x + ((e.clientX - r.left) / r.width) * this.view.w,
      y: this.view.y + ((e.clientY - r.top) / r.height) * this.view.h,
    };
  }

  /**
   * Place each label on the side of its pin where it hits neither another label
   * nor another pin nor the edge of the frame, and hide it rather than
   * overprint. Four pins never collide; twenty in the north-east always do, and
   * a label on top of another label is worse than no label because it is
   * unreadable AND it hides a pin.
   *
   * Everything here is in FRAME UNITS: 0..VIEW.w across whatever is currently
   * visible, 0..VIEW.h down it. That is the one space where all three tests are
   * meaningful at once — a label's offsets are counter-scaled so they are
   * already in these units, and the frame edge is a fixed number rather than a
   * function of the zoom level. Doing it in map units instead is what let a
   * label run 25px past the right-hand edge and get clipped.
   *
   * Greedy, in list order, so the result is stable between renders rather than
   * shuffling every time something is filtered.
   */
  #layoutLabels() {
    if (!this.svg) return;
    const v = this.view;
    const PAD = 3;
    // Six candidates, tried in order of how well a label reads when it is there:
    // beside the pin first, then below it, then stacked above or under.
    const CANDIDATES = [
      { dx: 14, dy: -13, anchor: 'start' },
      { dx: -14, dy: -13, anchor: 'end' },
      { dx: 14, dy: 20, anchor: 'start' },
      { dx: -14, dy: 20, anchor: 'end' },
      { dx: 0, dy: -32, anchor: 'middle' },
      { dx: 0, dy: 34, anchor: 'middle' },
    ];
    const hits = (a, b) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;

    const visible = [];
    for (const card of this.cards) {
      const g = card.__pin;
      if (!g || card.hidden) { if (g?.__label) g.__label.style.display = 'none'; continue; }
      const px = ((g.__x - v.x) / v.w) * VIEW.w;
      const py = ((g.__y - v.y) / v.h) * VIEW.h;
      // A pin scrolled out of the frame is clipped anyway. Skipping it here also
      // stops it reserving space that a pin the reader can actually see needs.
      if (px < -20 || px > VIEW.w + 20 || py < -20 || py > VIEW.h + 20) {
        if (g.__label) g.__label.style.display = 'none';
        continue;
      }
      visible.push({ g, px, py });
    }

    // Seed with the markers themselves so a label never lands on another pin.
    // Roughly the teardrop's own box; the online ring is smaller and fits inside.
    // Tagged with its owner, because a label is of course allowed to sit beside
    // its OWN pin — the default offset clears it by two pixels, and without the
    // exemption a slightly wider halo would start hiding labels for no reason a
    // reader could see.
    const taken = visible.map(({ g, px, py }) => ({ owner: g, x0: px - 12, y0: py - 29, x1: px + 12, y1: py + 3 }));

    for (const { g, px, py } of visible) {
      const t = g.__label;
      if (!t) continue;
      t.style.display = '';

      // Measured once, from the rendered text, and cached — the box is invariant
      // under zoom because the label counter-scales with the pin. A zero width
      // means the map is not rendered yet (a collapsed details element, say), so
      // fall back to an estimate for this pass WITHOUT caching it.
      let lb = g.__lb;
      if (!lb) {
        const b = t.getBBox();
        lb = b.width
          ? (g.__lb = { x: b.x, y: b.y, w: b.width, h: b.height })
          : { x: 0, y: -16, w: Math.max(...g.__lines.map((s) => s.length)) * 7.6, h: g.__lines.length * 17.25 };
      }

      let placed = null;
      for (const c of CANDIDATES) {
        const anchorShift = c.anchor === 'end' ? -lb.w : c.anchor === 'middle' ? -lb.w / 2 : 0;
        const x0 = px + c.dx + lb.x + anchorShift;
        const y0 = py + c.dy + lb.y;
        const box = { x0, y0, x1: x0 + lb.w, y1: y0 + lb.h };
        if (box.x0 < PAD || box.x1 > VIEW.w - PAD || box.y0 < PAD || box.y1 > VIEW.h - PAD) continue;
        if (taken.some((b) => b.owner !== g && hits(box, b))) continue;
        placed = { c, box };
        break;
      }

      if (!placed) { t.style.display = 'none'; continue; }
      taken.push(placed.box);
      t.setAttribute('x', placed.c.dx);
      t.setAttribute('text-anchor', placed.c.anchor);
      t.setAttribute('y', placed.c.dy);
      [...t.children].forEach((s, i) => { s.setAttribute('x', placed.c.dx); s.setAttribute('dy', i ? '1.15em' : 0); });
    }
  }

  /**
   * Fetch the postcode table, once, the first time somebody types something
   * postcode-shaped. It is ~54KB and the overwhelming majority of visitors never
   * search at all, so it has no business in the page's initial load.
   *
   * This never rejects. A failed fetch sets the table to null, which every
   * caller reads as "resolve nothing", and the search goes on doing the
   * substring filtering it did before the feature existed.
   */
  #loadPostcodes() {
    if (this.pcPending) return;
    this.pcPending = true;
    const url = this.dataset.postcodes;
    if (!url) { this.pcTable = null; return; }
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`postcodes ${res.status}`);
        return res.json();
      })
      .then((json) => { this.pcTable = json && typeof json === 'object' ? json : null; })
      .catch((err) => {
        // Warn once. Not retried: a store whose CDN is failing does not need
        // one more request per keystroke.
        console.warn('[dealer-locator] postcode table unavailable, search stays text-only:', err);
        this.pcTable = null;
      })
      .finally(() => this.#apply());
  }

  /**
   * A typed state or province -> its code on the map, via the map's own index.
   *
   * NAMES ONLY, never the two-letter abbreviation. "or", "hi", "in", "me", "pa"
   * and "de" are all real state codes and all real fragments of words somebody
   * is in the middle of typing — resolving them would fling the map to Oregon
   * on the way to "Ford". Abbreviations lose nothing by being excluded: the
   * region field is in `data-haystack`, so "AB" already finds the Calgary shop
   * by text. It is the full names that text matching cannot find, because the
   * haystack holds "CA" and never "California". No region on this map has a
   * name shorter than four characters.
   */
  #regionCodeFor(raw) {
    const r = normRegion(raw);
    if (!r || r.length < 3) return null;
    return this.byRegion.get(`|${r}`) || null;
  }

  /**
   * What was typed -> somewhere on Earth, or null for "just filter the text".
   *
   * Postcode first because it is the unambiguous one. A region is only accepted
   * on a WHOLE-STRING match against the map's own names and abbreviations, so
   * "Calgary" stays a text search rather than being read as a region.
   */
  #resolve(raw) {
    if (!raw) return null;

    const key = postcodeKey(raw);
    if (key) {
      if (this.pcTable === undefined) { this.#loadPostcodes(); return null; }
      const hit = this.pcTable?.[key];
      if (Array.isArray(hit) && hit.length === 2) {
        const us = /^\d/.test(key);
        return {
          lat: hit[0], lng: hit[1],
          label: raw.toUpperCase(),
          kind: 'postcode',
          code: us ? lobeHintFor(key) : 'CA-ZZ',
          units: us ? 'mi' : 'km',
        };
      }
      // A real postcode shape that is not in the table (a decommissioned
      // prefix, or one of the territories the table deliberately drops) falls
      // through to text rather than pretending nothing was typed.
    }

    const code = this.#regionCodeFor(raw);
    const path = code && this.byCode.get(code);
    if (path) {
      const b = path.getBBox();
      const ll = unproject(b.x + b.width / 2, b.y + b.height / 2, path.dataset.lobe);
      if (ll) {
        return {
          lat: ll[1], lng: ll[0],
          label: path.dataset.name,
          kind: 'region',
          code,
          units: path.dataset.country === 'CA' ? 'km' : 'mi',
        };
      }
    }
    return null;
  }

  /**
   * Mark where the search landed. Deliberately a crosshair and not a pin: "a pin
   * is a promise that there is a door", and there is no door at the centroid of
   * a postal district. It also sits UNDER the dealer pins, because it is a
   * reference point rather than a destination.
   */
  #markSearch(geo) {
    if (this.mark) { this.mark.remove(); this.mark = null; }
    if (!geo || !this.pinLayer) return;
    const xy = project(geo.lng, geo.lat, geo.code);
    if (!xy) return;

    const g = el('g', { class: 'dmfg-dl__seek' });
    g.__x = +xy[0].toFixed(1);
    g.__y = +xy[1].toFixed(1);
    g.appendChild(el('circle', { class: 'dmfg-dl__seek-ring', r: 9 }));
    g.appendChild(el('path', { class: 'dmfg-dl__seek-cross', d: 'M-15,0 H-5 M5,0 H15 M0,-15 V-5 M0,5 V15' }));
    const title = document.createElementNS(SVG_NS, 'title');
    title.textContent = `${geo.label} — approximate centre`;
    g.appendChild(title);

    this.pinLayer.prepend(g);
    this.mark = g;
    this.applyView?.();
  }

  /** Frame the answer: where they searched, and the nearest thing to it. */
  #frameResult(geo, nearest) {
    if (!this.mark || !this.applyView) return;
    const pts = [[this.mark.__x, this.mark.__y]];
    if (nearest?.__pin) pts.push([nearest.__pin.__x, nearest.__pin.__y]);
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const y0 = Math.min(...ys);
    const y1 = Math.max(...ys);
    // A floor on the padding, or a search that resolves right on top of a dealer
    // frames a zero-size box and slams to maximum zoom.
    const padX = Math.max((x1 - x0) * 0.25, 70);
    const padY = Math.max((y1 - y0) * 0.25, 70);
    this.#frame(x0 - padX, y0 - padY, x1 - x0 + padX * 2, y1 - y0 + padY * 2);
    // Remember that the SEARCH put the map here, so emptying the box can undo
    // it. Any deliberate zoom, drag or region click clears this again.
    this.viewFromSearch = true;
  }

  /** Reorder the list in place. Only when it actually changes — moving nodes
   *  scrolls the list back to the top and drops focus. */
  #order(cards) {
    if (!this.list) return;
    const key = cards.map((c) => c.__i).join(',');
    if (key === this.orderKey) return;
    this.orderKey = key;
    const frag = document.createDocumentFragment();
    for (const c of cards) frag.appendChild(c);
    this.list.appendChild(frag);
    if (this.noResults) this.list.appendChild(this.noResults);
  }

  /**
   * Pin popup — the dealer's own card, cloned onto the map.
   *
   * Cloned rather than re-rendered: the card already holds the name, the tier
   * badge, the address, the note and the four action buttons, all built from
   * the metaobject. Rebuilding that here would be a second copy of the same
   * markup, and a second copy is how a corrected phone number ends up right in
   * the list and wrong on the map.
   *
   * `[hidden]` children are skipped so the distance line — which only exists
   * after a postcode search — does not leave a blank row in the panel.
   */
  #openPopup(card, pin) {
    if (!this.popup || !this.popupBody) return;
    const clone = [...card.children]
      .filter((n) => !n.hasAttribute('hidden'))
      .map((n) => n.cloneNode(true));
    this.popupBody.replaceChildren(...clone);
    this.popupPin = pin;
    this.popup.hidden = false;
    this.#positionPopup();
  }

  #closePopup() {
    if (!this.popup || this.popup.hidden) return;
    this.popup.hidden = true;
    this.popupPin = null;
    this.popupBody?.replaceChildren();
  }

  /**
   * Anchor to the pin's TIP, taken from the SVG's own screen matrix rather
   * than the group's bounding box. The group contains the pin's text label,
   * which is wider than the pin and would drag the popup sideways by however
   * long the dealer's name happens to be.
   */
  #positionPopup() {
    const pop = this.popup, pin = this.popupPin;
    if (!pop || pop.hidden || !pin || !this.svg) return;
    const wrap = this.querySelector('.dmfg-dl__map-wrap');
    if (!wrap) return;

    const ctm = this.svg.getScreenCTM();
    if (!ctm) return;
    const pt = this.svg.createSVGPoint();
    pt.x = pin.__x;
    pt.y = pin.__y;
    const tip = pt.matrixTransform(ctm);

    const wr = wrap.getBoundingClientRect();
    const cx = tip.x - wr.left;
    const cy = tip.y - wr.top;

    // Measure before placing. Width is fixed by CSS, height is not.
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;

    const GAP = 14;      // clear of the pin tip
    const HEAD = 26;     // the pin body sits above its tip
    const EDGE = 8;

    let left = cx - pw / 2;
    left = Math.max(EDGE, Math.min(left, wr.width - pw - EDGE));

    // Above the pin by default; flip below only when it will not fit.
    let top = cy - HEAD - GAP - ph;
    const below = top < EDGE;
    if (below) top = cy + GAP;
    top = Math.max(EDGE, Math.min(top, Math.max(EDGE, wr.height - ph - EDGE)));

    pop.style.left = `${Math.round(left)}px`;
    pop.style.top = `${Math.round(top)}px`;
    pop.classList.toggle('is-below', below);

    // Keep the tail pointing at the pin even when the panel has been pushed
    // against a map edge and is no longer centred on it.
    const tail = Math.max(14, Math.min(cx - left, pw - 14));
    pop.style.setProperty('--tail-x', `${Math.round(tail)}px`);
  }

  #focus(card, pin) {
    this.cards.forEach((c) => {
      c.classList.toggle('is-active', c === card);
      c.__pin?.classList.toggle('is-active', c === card);
    });
    card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    pin?.parentNode?.appendChild(pin); // raise above its neighbours
    if (pin) this.#openPopup(card, pin); else this.#closePopup();
    // Selecting something off-frame while zoomed in should bring it into view,
    // not just highlight a card for a pin nobody can see.
    if (pin && this.view && this.zoomed) {
      const v = this.view;
      const margin = v.w * 0.12;
      if (pin.__x < v.x + margin || pin.__x > v.x + v.w - margin
        || pin.__y < v.y + margin || pin.__y > v.y + v.h - margin) {
        v.x = pin.__x - v.w / 2;
        v.y = pin.__y - v.h / 2;
        this.#clamp();
        this.applyView();
      }
    }
  }

  /**
   * Shade a region by how many places in it you could actually walk into —
   * stocking dealers, authorized installers, and the shop itself. An online retailer has no
   * storefront, so it must not shade: the map would be claiming reach that is
   * not there.
   */
  #shade(shown) {
    if (!this.states.length) return;
    const count = new Map();
    for (const card of shown) {
      if (!WALK_IN.has(card.dataset.tier) || !card.__code) continue;
      count.set(card.__code, (count.get(card.__code) || 0) + 1);
    }
    for (const p of this.states) {
      const n = count.get(p.dataset.code) || 0;
      p.setAttribute('data-level', n === 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : 3);
      const title = p.querySelector('title');
      if (title) {
        // "location", not "dealer" — the Utah pin is the shop itself, and
        // calling Marcus's own bench a dealer would be the map inventing a
        // relationship that does not exist.
        title.textContent = n
          ? `${p.dataset.name} — ${n} location${n === 1 ? '' : 's'} you can visit`
          : `${p.dataset.name} — nowhere to visit yet`;
      }
    }
  }

  #apply() {
    const raw = (this.search?.value || '').trim();
    const q = raw.toLowerCase();
    const geo = this.#resolve(raw);
    const shown = [];

    for (const card of this.cards) {
      const tierOk = this.tier === 'all' || card.dataset.tier === this.tier;
      // In GEO mode the substring filter is deliberately OFF. The whole point of
      // resolving a postcode is that 84070 should find the shop five miles away,
      // and no dealer's haystack contains a postcode they are merely near.
      const textOk = geo ? true : !q || (card.dataset.haystack || '').includes(q);
      const on = tierOk && textOk;
      card.hidden = !on;
      if (card.__pin) card.__pin.hidden = !on;
      if (on) shown.push(card);

      card.__dist = null;
      if (geo) {
        const lat = parseFloat(card.dataset.lat);
        const lng = parseFloat(card.dataset.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          card.__dist = haversineMi(geo.lat, geo.lng, lat, lng);
        }
      }
    }

    // Nearest first. A location with no coordinates cannot be ranked, so it goes
    // last rather than being dropped — it is still a real place to buy from.
    const ordered = geo
      ? shown.slice().sort((a, b) => (a.__dist ?? Infinity) - (b.__dist ?? Infinity))
      : shown;
    this.#order(geo ? [...ordered, ...this.cards.filter((c) => c.hidden)] : this.cards);
    this.#distances(geo);

    this.#markSearch(geo);
    const gk = geo ? `${geo.lat},${geo.lng}` : null;
    if (gk && gk !== this.geoKey) {
      this.#frameResult(geo, ordered.find((c) => c.__pin && c.__dist != null));
    } else if (!raw && this.viewFromSearch && this.applyView) {
      // Emptying the box gives the whole map back — but only if the search is
      // what moved the map in the first place. Zooming by hand and then
      // clicking a tier filter must not throw the reader's own view away, and
      // going postcode -> plain text -> empty still counts as coming from a
      // search even though the last state before empty was a text filter.
      this.view = { x: 0, y: 0, w: VIEW.w, h: VIEW.h };
      this.viewFromSearch = false;
      this.applyView();
    }
    this.geoKey = gk;

    this.#shade(shown);
    this.#layoutLabels();
    this.#geoNote(geo, ordered);

    if (this.noResults) this.noResults.hidden = shown.length !== 0;
    if (this.countEl) {
      // "locations", not "dealers" — the list now includes Marcus's own shop,
      // and it is not one of his dealers.
      const total = this.cards.length;
      this.countEl.textContent = shown.length === total
        ? `${total} ${total === 1 ? 'location' : 'locations'}`
        : `${shown.length} of ${total} locations`;
    }
  }

  /** Put the number on the card. A sorted list with no numbers asks the reader
   *  to take the order on trust. */
  #distances(geo) {
    const toUnit = (mi) => (geo?.units === 'km' ? mi / MI_PER_KM : mi);
    for (const card of this.cards) {
      const out = card.querySelector('[data-dist]');
      if (!out) continue;
      if (geo && card.__dist != null) {
        out.textContent = `${Math.round(toUnit(card.__dist)).toLocaleString()} ${geo.units} away`;
        out.hidden = false;
      } else {
        out.textContent = '';
        out.hidden = true;
      }
    }
  }

  /**
   * Say what was resolved and how precisely, because the lookup is a postal
   * DISTRICT and not an address — `build-postcode-table.mjs` measures the
   * aggregation error at a 19-mile median and says in as many words that the UI
   * must not claim precision.
   *
   * Also carries the "nothing near you" route to buying direct. With four
   * locations on a continent that is the common case, and the honest answer is
   * more use to the reader than a nearest-dealer number that means nothing.
   */
  #geoNote(geo, ordered) {
    if (!this.geoEl) return;
    if (!geo) {
      this.geoEl.hidden = true;
      this.geoEl.textContent = '';
      return;
    }
    const u = geo.units;
    const conv = (mi) => Math.round(u === 'km' ? mi / MI_PER_KM : mi).toLocaleString();
    const walkIn = ordered.find((c) => WALK_IN.has(c.dataset.tier) && c.__dist != null);

    /* These four sentences are Marcus's, from the Installing Dealers section
     * settings, and arrive as data attributes on <dealer-locator>.
     *
     * The first is what stops a customer reading a straight-line number as a
     * drive; the last names the closest place they can actually walk into.
     * Wording a customer relies on in order not to drive to the wrong town does
     * not belong in a 1,100-line asset. Missing attribute falls back to the
     * original string, so an older template still reads correctly. */
    const fill = (tpl, vars) =>
      Object.entries(vars).reduce((acc, [k, v]) => acc.split(`[[${k}]]`).join(v), tpl);

    let s = geo.kind === 'postcode'
      ? fill(this.dataset.notePostcode
          || 'Straight-line distance from the middle of postcode [[place]] — that is a postal area, not your address.',
        { place: geo.label })
      : fill(this.dataset.notePlace
          || 'Straight-line distance from the middle of [[place]].',
        { place: geo.label });

    if (!walkIn) {
      s += ' ' + (this.dataset.noteNoWalkin
        || 'Nothing here is a place you can walk into. Every part ships direct from the shop.');
    } else if (walkIn.__dist > NEAR_RADIUS_MI) {
      s += ' ' + fill(this.dataset.noteFarWalkin
        || 'Nothing within [[radius]] [[units]]. The closest place you can walk into is [[dealer]], [[distance]] [[units]] away — or buy direct and have it shipped.',
      { radius: conv(NEAR_RADIUS_MI), units: u, dealer: walkIn.dataset.name, distance: conv(walkIn.__dist) });
    }

    this.geoEl.textContent = s;
    this.geoEl.hidden = false;
  }
}

if (!customElements.get('dealer-locator')) {
  customElements.define('dealer-locator', DealerLocator);
}
